import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import MonitoringContext from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/monitoring-context';
import ReportContext from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/report-context';

import Icons from 'assets/icons';

import type { ContextMetadata } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/monitoring-context';
import type { InsightDto } from 'api/generated/schemas/insightDto';
import type { UserInsightDto } from 'api/generated/schemas/userInsightDto';

type ContextDataPanelInsight = InsightDto | UserInsightDto;

interface ContextDataPanelProps {
  insight: ContextDataPanelInsight | null | undefined;
  isAdminView?: boolean;
}

const ContextDataPanel = ({
  insight,
  isAdminView = true,
}: ContextDataPanelProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const metadata = (insight?.metadata || {}) as ContextMetadata;
  const isReport = metadata?.insightType === 'report';

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded, metadata]);

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center gap-2 text-left transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="font-semibold text-navy-800 text-lg">
          Context and data
        </span>
        <Icons.Arrow
          customClass={classNames('size-4 transition-transform duration-200', {
            'rotate-[-90deg]': isExpanded,
            'rotate-90': !isExpanded,
          })}
        />
      </button>

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ height: `${contentHeight}px` }}
      >
        <div
          ref={contentRef}
          className={classNames('px-4 pb-4', { hidden: !isExpanded })}
        >
          <div className="pt-2" tabIndex={!isExpanded ? -1 : undefined}>
            {isReport ? (
              <ReportContext
                metadata={metadata}
                clientId={insight?.clientId}
                sourceId={metadata?.sourceId}
                isAdminView={isAdminView}
              />
            ) : (
              <MonitoringContext metadata={metadata} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextDataPanel;
